/**
 * <QuizReviewQuestion> — une question du quiz de prérequis, relue (molecule).
 *
 * La ligne de l'ancien `PrerequisiteQuizDisplay`, côté élève : repliée, le numéro de la question
 * (à la couleur de ce qu'elle a donné), son énoncé, ce que l'élève a répondu (« Pas de réponse »
 * s'il n'a rien répondu), juste ou faux, et le chevron ; dépliée, toutes ses réponses, comme le
 * « Défi éclair » les marque une fois répondu (`<QuizChoiceRow>`) : la bonne en vert, celle que
 * l'élève a choisie à tort en rouge, les autres éteintes.
 *
 * Ce qui change : la bonne réponse que l'élève n'a pas choisie est verte, comme dans le défi (elle
 * était marine) ; « → 3 » devient « Ta réponse : 3 » ; un énoncé à formule (`textFormat="rich"`) se
 * rend comme celui d'une activité (`<RichText>`, Wajih, 18/10), les autres restent du texte. La
 * réponse donnée aussi (`answerFormat="rich"`), à la taille et à l'encre de sa ligne.
 *
 * `attempted={false}` (le masteur relit un quiz que l'élève n'a pas encore tenté) : rien n'est juste ni faux, la
 * question est neutre — son numéro en bleu, ni coche ni croix, pas de « Pas de réponse » (test du 04/11 après-midi,
 * Wajih : le rouge disait un échec qui n'avait pas eu lieu). La bonne réponse reste marquée, dépliée.
 *
 * Ouverte ou fermée est un état d'interface : il vit ici (`<Accordion>`). `defaultExpanded` l'ouvre
 * d'emblée.
 *
 * `compact` (le quiz relu au centre du live) : la densité de l'ancienne fiche — repliée, l'énoncé et
 * la réponse tiennent chacun sur une ligne (l'énoncé entier se lit une fois dépliée), un texte plus
 * petit, des réponses en lignes denses (`<QuizChoiceRow size="sm">`). `trailing` pose une action au
 * bout de la ligne, hors du toucher qui déplie (le drapeau « Signaler » du tuteur).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { Accordion } from "../Accordion"
import { QuizChoiceRow, type QuizChoiceState } from "../QuizChoiceRow"
import { quizChoiceLetter } from "../QuizChoiceRow/QuizChoiceRow"
import { RichText } from "../RichText"
import {
    type UIQuizReviewOptionMark,
    type UIQuizReviewQuestion,
} from "../../../ports/primary/ui/types/studentQuizReview"

export type QuizReviewQuestionProps = {
    /** Sa place dans le quiz, à partir de 0 (« Q1 » pour la première). */
    index: number
    question: UIQuizReviewQuestion
    /** Dépliée d'emblée. Défaut `false`, comme avant. */
    defaultExpanded?: boolean
    /**
     * À qui appartient la réponse : `student` (« Ta réponse », l'élève relit son quiz, défaut) ou
     * `tutor` (« Sa réponse », le masteur relit celui d'un élève en live).
     */
    reader?: "student" | "tutor"
    /** La densité de l'ancienne fiche (le centre du live). Défaut `false`. */
    compact?: boolean
    /** Une action au bout de la ligne, hors du toucher qui déplie. Absente : rien. */
    trailing?: React.ReactNode
    /** L'élève l'a tentée. `false` : la question est neutre, ni juste ni fausse. Défaut `true`. */
    attempted?: boolean
    testID?: string
}

const TEXTS = {
    number: (index: number) => `Q${index + 1}`,
    answer: (answer: string) => `Ta réponse : ${answer}`,
    theirAnswer: (answer: string) => `Sa réponse : ${answer}`,
    unanswered: "Pas de réponse",
}

/**
 * Ce que dit la question, d'un mot : juste, fausse (rien répondu compris), ou pas encore tentée (neutre) — son numéro
 * et sa marque au bout de la ligne.
 */
const TONE = {
    right: { badge: "success", mark: { name: "check", color: "success-strong" } },
    wrong: { badge: "danger", mark: { name: "times", color: "danger-strong" } },
    pending: { badge: "info", mark: null },
} as const

/** La marque d'une réponse, en état de la ligne du défi. */
const CHOICE_STATE: Record<UIQuizReviewOptionMark, QuizChoiceState> = {
    right: "right",
    wrong: "wrong",
    none: "inert",
}

export function QuizReviewQuestion(props: QuizReviewQuestionProps) {
    const {
        index,
        question,
        defaultExpanded = false,
        reader = "student",
        compact = false,
        trailing,
        attempted = true,
        testID,
    } = props
    const answerLabel = reader === "tutor" ? TEXTS.theirAnswer : TEXTS.answer
    const { theme } = useUnistyles()
    const tone = TONE[!attempted ? "pending" : question.result === "right" ? "right" : "wrong"]

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            testID={testID}
            headerTrailing={trailing}
            header={(expanded) => (
                <View style={styles.header(compact)}>
                    <Badge text={TEXTS.number(index)} variant={tone.badge} tinted />
                    <Stack gap="1" style={styles.text}>
                        {question.textFormat === "rich" ? (
                            // La WebView de la formule ne prend pas le toucher : il replie la question. L'en-tête est
                            // un bouton, qui centre son texte sur le web : la question reste au début de la ligne.
                            <View pointerEvents="none">
                                <RichText value={question.text} align="start" compact />
                            </View>
                        ) : (
                            <Text
                                size={compact ? "text-md" : "text-base"}
                                weight="semibold"
                                color="text-strong"
                                numberOfLines={compact && !expanded ? 1 : 0}
                            >
                                {question.text}
                            </Text>
                        )}
                        {!attempted ? null : question.answer != null && question.answerFormat === "rich" ? (
                            <View pointerEvents="none">
                                <RichText
                                    value={answerLabel(question.answer)}
                                    reading={{ adapted: false, font: "standard", size: theme.fontSizes["text-sm"] }}
                                    color="text-muted"
                                    align="start"
                                    compact
                                />
                            </View>
                        ) : (
                            <Text size="text-sm" color="text-muted" numberOfLines={compact && !expanded ? 1 : 0}>
                                {question.answer != null ? answerLabel(question.answer) : TEXTS.unanswered}
                            </Text>
                        )}
                    </Stack>
                    <Row gap="2">
                        {tone.mark != null && (
                            <Icon name={tone.mark.name} size={compact ? 16 : 18} color={tone.mark.color} />
                        )}
                        <Icon
                            name={expanded ? "chevron-up" : "chevron-down"}
                            size={compact ? 14 : 16}
                            color="text-muted"
                        />
                    </Row>
                </View>
            )}
        >
            <Stack gap={compact ? "1" : "2"} style={styles.choices(compact)}>
                {question.options.map((option, i) => (
                    <QuizChoiceRow
                        key={`${i}-${option.label}`}
                        letter={quizChoiceLetter(i)}
                        label={option.label}
                        labelFormat={option.labelFormat}
                        state={CHOICE_STATE[option.mark]}
                        size={compact ? "sm" : "md"}
                    />
                ))}
            </Stack>
        </Accordion>
    )
}

const styles = StyleSheet.create((theme) => ({
    header: (compact: boolean) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing[compact ? "2" : "3"],
        paddingVertical: theme.spacing[compact ? "2" : "3"],
    }),
    text: { flex: 1, flexShrink: 1 },
    choices: (compact: boolean) => ({ paddingBottom: theme.spacing[compact ? "2" : "3"] }),
}))
